import { AxiosResponse } from 'axios';
import { call, fork, put, takeEvery } from 'redux-saga/effects';

import { SettingsPlansDetails } from '../domain/settingsPlansDetails';

import {
  FetchLatestSettingsPlansDetailsAction,
  FetchSettingsPlansDetailsAction,
  PatchSettingsPlansPredictRequestAction,
  SettingsPlansDetailsActionCreators,
  SettingsPlansDetailsActionTypes,
} from '../redux/server/settingsPlansDetails';
import { Api, buildConfig } from '../utils/api';
import { handleErrorSaga } from './errorSaga';

/**
 * 予定詳細データを取得するSaga
 * GET /settings/plans/{year}/{month}/{hallCode} を呼び出す
 * @param api AxiosInstance
 * @param action FetchSettingsPlansDetailsAction
 * @param action.payload.params URLパスに使用するパラメータ（year, month, hallCode）
 * @param action.payload.queryParams クエリパラメータ（isLatestが指定されている場合のみ含まれる）
 */
export function* fetchSettingsPlansDetailsSaga(
  api: Api,
  action: FetchSettingsPlansDetailsAction
) {
  const { year, month, hallCode } = action.payload.params;
  const queryParams = action.payload.queryParams;
  try {
    yield put(
      SettingsPlansDetailsActionCreators.fetchSettingsPlansDetailsRequestAction()
    );
    const response: AxiosResponse<SettingsPlansDetails> = yield call(
      api.get,
      `/settings/plans/${year}/${month}/${hallCode}`,
      buildConfig(queryParams)
    );
    const settingsPlans = response.data;
    yield put(
      SettingsPlansDetailsActionCreators.fetchSettingsPlansDetailsSuccessAction(
        settingsPlans
      )
    );
  } catch (error: unknown) {
    yield put(
      SettingsPlansDetailsActionCreators.renewSettingsPlansDetailsACtion()
    );
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 予測値を確定するSaga
 * PATCH /settings/plans/{hallCode}/predict を呼び出す
 * 予定登録画面で表示された予測値を確定する際に使用される
 * @param api AxiosInstance
 * @param action PatchSettingsPlansPredictRequestAction
 * @param action.payload.hallCode 店舗コード
 */
export function* patchSettingsPlansPredictSaga(
  api: Api,
  action: PatchSettingsPlansPredictRequestAction
) {
  const { hallCode } = action.payload;
  try {
    yield call(
      api.patch,
      `/settings/plans/${hallCode}/predict`,
      {},
      buildConfig()
    );
    yield put(
      SettingsPlansDetailsActionCreators.patchSettingsPlansPredictSuccessAction()
    );
  } catch (error: unknown) {
    yield put(
      SettingsPlansDetailsActionCreators.patchSettingsPlansPredictFailureAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

/**
 * 最新の予測データを取得するSaga（isLatest=true）
 * 既存のsettingsPlansDetailsを上書きせず、latestSettingsPlansDetailsに保存する
 */
export function* fetchLatestSettingsPlansDetailsSaga(
  api: Api,
  action: FetchLatestSettingsPlansDetailsAction
) {
  const { year, month, hallCode } = action.payload.params;
  try {
    yield put(
      SettingsPlansDetailsActionCreators.fetchLatestSettingsPlansDetailsRequestAction()
    );
    const response: AxiosResponse<SettingsPlansDetails> = yield call(
      api.get,
      `/settings/plans/${year}/${month}/${hallCode}`,
      buildConfig({ isLatest: true })
    );
    yield put(
      SettingsPlansDetailsActionCreators.fetchLatestSettingsPlansDetailsSuccessAction(
        response.data
      )
    );
  } catch (error: unknown) {
    yield put(
      SettingsPlansDetailsActionCreators.fetchLatestSettingsPlansDetailsFailureAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchSettingsPlansDetailsSaga(api: Api) {
  yield takeEvery(
    SettingsPlansDetailsActionTypes.FETCH_SETTINGS_PLANS_DETAILS,
    fetchSettingsPlansDetailsSaga,
    api
  );
}

function* handleFetchLatestSettingsPlansDetailsSaga(api: Api) {
  yield takeEvery(
    SettingsPlansDetailsActionTypes.FETCH_LATEST_SETTINGS_PLANS_DETAILS,
    fetchLatestSettingsPlansDetailsSaga,
    api
  );
}

function* handlePatchSettingsPlansPredictSaga(api: Api) {
  yield takeEvery(
    SettingsPlansDetailsActionTypes.PATCH_SETTINGS_PLANS_PREDICT_REQUEST,
    patchSettingsPlansPredictSaga,
    api
  );
}

export function* settingsPlansDetailsSagas(context: { api: Api }) {
  yield fork(handleFetchSettingsPlansDetailsSaga, context.api);
  yield fork(handleFetchLatestSettingsPlansDetailsSaga, context.api);
  yield fork(handlePatchSettingsPlansPredictSaga, context.api);
}
